/**
 * The date the customer writes next to their signature.
 *
 * On paper the customer dates the contract themselves, and Jonathon asked for
 * the same on the screen (2026-08-16: "I want the customer to esign it and
 * select the date"). So there is a date field, it defaults to today, and they
 * can change it.
 *
 * Two things keep that from being a hole:
 *
 *  1. **It is not the audit record.** The server stamps its own clock at the
 *     moment the acceptance is written and stores that separately. The
 *     customer's date is what the contract says; the timestamp is what
 *     happened. A dispute has both, and they are never conflated.
 *  2. **It is bounded.** A window of a week back and a day forward covers every
 *     honest reason the two could differ — a customer whose phone is in another
 *     timezone, a rep who left the link with somebody over a long weekend, a
 *     signature at one minute to midnight — and nothing else. Dating a contract
 *     three months ago is not a thing a homeowner needs to do, and the three
 *     business days to cancel run from this date, so letting it wander backwards
 *     would let a signature arrive with the cancellation window already shut.
 *
 * The window is enforced in three places for the usual reason: the page draws
 * it, the Edge Function re-checks it because the page is JavaScript on somebody
 * else's phone, and the Edge Function's copy is bound to this one by
 * supabase/test/signed-date-window-match.test.ts.
 */

/** How far back the customer may date a signature, in days. */
export const SIGNED_DATE_BACKDATE_DAYS = 7;

/** How far forward. One day, for the phone that thinks it is already tomorrow. */
export const SIGNED_DATE_FORWARD_DAYS = 1;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD` as the calendar where the reader is standing reads it. */
export function localDateString(now: Date): string {
  const shifted = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return shifted.toISOString().slice(0, 10);
}

/**
 * `value` as an instant, where a bare `YYYY-MM-DD` is midnight where the
 * reader is standing. `new Date("2026-09-01")` is midnight UTC, the evening
 * before anywhere in the United States, so a `date` column compared against a
 * local window start lands a day early. A real timestamp is read as it is.
 */
export function asLocalInstant(value: string): Date {
  return DATE_ONLY.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);
}

/** `date` moved by `days`, staying a plain calendar date. */
function shift(date: string, days: number): string {
  const at = new Date(`${date}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * The earliest and latest date a signature may carry, given today.
 *
 * `today` is passed in rather than read from a clock so this stays pure, and so
 * the page and the server can each supply their own — they are in different
 * timezones and both are entitled to be right.
 */
export function signedDateWindow(today: string): { min: string; max: string } {
  return {
    min: shift(today, -SIGNED_DATE_BACKDATE_DAYS),
    max: shift(today, SIGNED_DATE_FORWARD_DAYS),
  };
}

/** Whether a customer's date is a real date inside the window. */
export function signedDateIsValid(value: unknown, today: string): boolean {
  if (typeof value !== "string" || !DATE_ONLY.test(value)) return false;
  // Rejects 2026-02-31 and friends: Date normalises them, so a round trip that
  // does not come back the same string was never a date.
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  if (parsed.toISOString().slice(0, 10) !== value) return false;

  const { min, max } = signedDateWindow(today);
  return value >= min && value <= max;
}
